import React, { useEffect, useState, useRef } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { CONSTRUCTION_PERIOD_OPTIONS, HOUSE_FLOORS } from '../../lib/constants';
import { cn } from '../../lib/utils';
interface StepFloorYearProps {
  housingType: string;
  floor: string;
  constructionYear: string;
  onChange: (patch: {
    floor?: string;
    constructionYear?: string;
  }) => void;
}
const APARTMENT_FIXED_PILLS = [{
  value: 'demisol',
  label: 'Demisol'
}, {
  value: 'parter',
  label: 'Parter'
}, {
  value: 'mansarda',
  label: 'Mansardă'
}];
// Apartment-style floor values are stored as one of:
//   'demisol' | 'parter' | 'mansarda' | 'floor-<N>'
function parseApartmentFloor(value: string): {
  pill: string | null;
  number: string;
} {
  if (!value) return {
    pill: null,
    number: ''
  };
  if (value.startsWith('floor-')) {
    return {
      pill: null,
      number: value.slice('floor-'.length)
    };
  }
  if (APARTMENT_FIXED_PILLS.some(p => p.value === value)) {
    return {
      pill: value,
      number: ''
    };
  }
  return {
    pill: null,
    number: ''
  };
}
export function StepFloorYear({
  housingType,
  floor,
  constructionYear,
  onChange
}: StepFloorYearProps) {
  const isHouse = housingType === 'house';
  const parsed = parseApartmentFloor(floor);
  const [floorNumber, setFloorNumber] = useState<string>(parsed.number);
  // Keep local input in sync if floor changes externally (edit mode prefill).
  useEffect(() => {
    setFloorNumber(parseApartmentFloor(floor).number);
  }, [floor]);
  const pickPill = (val: string) => {
    setFloorNumber('');
    onChange({
      floor: val
    });
  };
  const handleNumberChange = (raw: string) => {
    // Sanitize: keep digits only, clamp 1..30.
    const digits = raw.replace(/\D/g, '').slice(0, 2);
    setFloorNumber(digits);
    if (!digits) {
      // If we cleared the input but a numeric floor was set, clear it.
      if (floor.startsWith('floor-')) onChange({
        floor: ''
      });
      return;
    }
    const n = parseInt(digits, 10);
    if (Number.isNaN(n) || n < 1 || n > 30) return;
    onChange({
      floor: `floor-${n}`
    });
  };
  return <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-10" data-id="element-2510">
      <div data-id="element-2511">
        <h2 className="text-2xl sm:text-3xl font-bold text-light-1 mb-2" data-id="element-2512">
          {isHouse ? 'Câte etaje are casa?' : 'La ce etaj se află?'}
        </h2>
        <p className="text-sm text-light-3" data-id="element-2513">
          {isHouse ? 'Structura proprietății, nu poziția pe scară.' : 'Etajul exact, așa cum apare în actele proprietății.'}
        </p>
      </div>

      {isHouse ? <PillGroup label="Etaje" value={floor} options={HOUSE_FLOORS as any} onChange={v => onChange({
      floor: v
    })} data-id="element-2514" /> : <div className="space-y-5" data-id="element-2515">
          <h3 className="text-xs font-medium text-light-3 uppercase tracking-wider" data-id="element-2516">
            Etaj
          </h3>
          <div className="flex flex-wrap gap-2" data-id="element-2517">
            {APARTMENT_FIXED_PILLS.map(opt => {
          const isSelected = parsed.pill === opt.value;
          return <button key={opt.value} type="button" onClick={() => pickPill(opt.value)} className={cn('h-11 px-5 rounded-full text-sm font-medium border transition-colors cursor-pointer', isSelected ? 'bg-lime-1 border-lime-1 text-dark-1' : 'bg-dark-2 border-dark-4 text-light-2 hover:border-light-3')} data-id="element-2518">
                  {opt.label}
                </button>;
        })}
          </div>

          <div className="flex items-center gap-3" data-id="element-2519">
            <label htmlFor="floor-number" className="text-sm text-light-3 shrink-0" data-id="element-2520">
              Sau alt etaj
            </label>
            <input id="floor-number" type="number" min={1} max={30} inputMode="numeric" value={floorNumber} onChange={e => handleNumberChange(e.target.value)} placeholder="ex. 4" className="w-24 h-11 px-3 rounded-md bg-dark-2 border border-dark-4 text-sm text-light-1 placeholder:text-light-4 focus:outline-none focus:border-lime-1 transition-colors" data-id="element-2521" />
            {floor.startsWith('floor-') && <span className="inline-flex items-center h-9 px-3 rounded-full bg-lime-1 text-dark-1 text-xs font-semibold" data-id="element-2522">
                etaj {floor.slice('floor-'.length)}
              </span>}
          </div>
        </div>}

      <PeriodDropdown value={constructionYear} onChange={v => onChange({
      constructionYear: v
    })} data-id="element-2523" />
    </div>;
}
function PillGroup({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: {
    value: string;
    label: string;
  }[];
  onChange: (v: string) => void;
}) {
  return <div data-id="element-2524">
      <h3 className="text-xs font-medium text-light-3 uppercase tracking-wider mb-3" data-id="element-2525">
        {label}
      </h3>
      <div className="flex flex-wrap gap-2" data-id="element-2526">
        {options.map(opt => <button key={opt.value} type="button" onClick={() => onChange(opt.value)} className={cn('h-11 px-5 rounded-full text-sm font-medium border transition-colors cursor-pointer', value === opt.value ? 'bg-lime-1 border-lime-1 text-dark-1' : 'bg-dark-2 border-dark-4 text-light-2 hover:border-light-3')} data-id="element-2527">
            {opt.label}
          </button>)}
      </div>
    </div>;
}
function PeriodDropdown({
  value,
  onChange
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const selected = CONSTRUCTION_PERIOD_OPTIONS.find(o => o.value === value);
  return <div className="relative" ref={ref} data-id="element-2528">
      <label className="block text-xs font-medium text-light-3 uppercase tracking-wider mb-2" data-id="element-2529">
        Perioadă construcție
      </label>
      <button type="button" onClick={() => setIsOpen(v => !v)} aria-haspopup="listbox" aria-expanded={isOpen} className={cn('w-full h-11 px-4 rounded-md text-sm flex items-center justify-between transition-colors cursor-pointer', 'bg-dark-1 border', value ? 'border-lime-1 text-light-1' : 'border-dark-4 text-light-3 hover:border-light-4')} data-id="element-2530">
        <span className="truncate pr-2 text-left" data-id="element-2531">
          {selected ? selected.label : 'Alege perioada…'}
        </span>
        <CaretDown size={14} weight="bold" className={cn('shrink-0 text-light-3 transition-transform', isOpen && 'rotate-180')} data-id="element-2532" />
      </button>

      {isOpen && <ul role="listbox" className="absolute top-full left-0 right-0 mt-2 z-50 max-h-72 overflow-y-auto rounded-xl bg-dark-2 border border-white/15 py-1 animate-in fade-in slide-in-from-top-2 dark-scrollbar" data-id="element-2533">
          {CONSTRUCTION_PERIOD_OPTIONS.map(opt => {
        const isSelected = value === opt.value;
        return <li key={opt.value} data-id="element-2534">
                <button type="button" onClick={() => {
            onChange(opt.value);
            setIsOpen(false);
          }} className={cn('w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer flex items-center gap-3', isSelected ? 'bg-dark-3 text-light-1' : 'text-light-2 hover:bg-dark-3 hover:text-light-1')} data-id="element-2535">
                  <div className={cn('w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors', isSelected ? 'border-lime-1' : 'border-light-3')} data-id="element-2536">
                    {isSelected && <div className="w-2 h-2 rounded-full bg-lime-1" data-id="element-2537" />}
                  </div>
                  {opt.label}
                </button>
              </li>;
      })}
        </ul>}
    </div>;
}